import React, { useMemo, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  RouterProvider,
  PageViewAnalytics,
  usePathname,
  useNavigate,
  useSearchParams,
  scrollToTop,
  findMatchingRoute,
  matchPath,
} from "@page-speed/router";
import type { Route } from "@page-speed/router";
import { initSuperflow } from "@usesuperflow/client";
import { BlocksRenderer, parseDesignPayload } from "@page-speed/blocks";
import { resolveBlocks } from "@page-speed/blocks/data";
import type { DesignPayload } from "@page-speed/blocks/types";
import { initOpenSiteRenderers } from "./block-registry";

// Register all @opensite/ui block components into @page-speed/blocks registry
// at module load time — before any React rendering occurs.
// @page-speed/blocks also auto-initializes its own built-in renderers on import.
initOpenSiteRenderers();

// ─── Types ────────────────────────────────────────────────────────────────

type LegacyRouteEntry = any[];

type EnhancedRouteEntry = {
  pageId?: number | string;
  pageIds?: Array<number | string>;
  designPayload?: DesignPayload | string | null;
  blocks?: any[] | null;
  // Injected blog-detail 404 marker (FEED_CONTRACT §5.3 rule 2).
  notFound?: boolean;
  // Site chrome harvested server-side for a not-found entry (whose `blocks` stay
  // empty because the 404 state is rendered by the client). Present only on the
  // injected blog-detail 404 entry.
  chrome?: EntryChrome | null;
};

// Harvested navbar/footer runs (customer-sites ChromeBlocks / BlogDetailEntry).
type EntryChrome = {
  header?: any[] | null;
  footer?: any[] | null;
};

type RouteConfigValue = LegacyRouteEntry | EnhancedRouteEntry | null | undefined;
type RoutingConfig = Record<string, RouteConfigValue>;

// Layout blocks for shared header/footer (keyed as "_layout" in routing config)
type LayoutBlock = {
  block_name?: string;
  block_ref?: string;
  data?: Record<string, unknown>;
  [key: string]: unknown;
};
type LayoutConfig = {
  header?: LayoutBlock[] | null;
  footer?: LayoutBlock[] | null;
};

// Dynamic route contract (FEED_CONTRACT §5.2). `_routes` declares patterns the
// client router registers; `_templates` holds the block trees each route renders.
type RouteDescriptor = {
  pattern: string;
  template: string;
  source?: string;
};
type DetailTemplate = { blocks?: any[] | null };
type DetailTemplates = Record<string, DetailTemplate | undefined>;

type RoutingConfigWithLayout = RoutingConfig & {
  _layout?: LayoutConfig;
  _routes?: RouteDescriptor[];
  _templates?: DetailTemplates;
};

type NormalizedRoute = {
  pageId?: string;
  blocks?: any[];
  designPayload?: DesignPayload;
};

// Feed API config (FEED_CONTRACT §7). Server-injected — never hardcoded in JS
// (legacy bug #6). Falls back to the website token when only that is present.
type FeedConfig = {
  baseUrl: string;
  websiteToken: string;
};

type WebsiteMetadata = {
  sf_project_id?: string | null;
  // Website UUID token (websites.token) — used by PageViewAnalytics, which
  // resolves websites by token, NOT by the numeric website id.
  token?: string | null;
};

declare global {
  interface Window {
    _dashtrackWebsite?: WebsiteMetadata;
    _dashtrackFeeds?: FeedConfig;
  }
}

// ─── Superflow (staging-only feedback overlay) ────────────────────────────

const SUPERFLOW_API_KEY = "gAPhbZ9G1PsvBXtDaVzE";

const useSuperflow = (projectId?: string | null) => {
  const hostname =
    typeof window !== "undefined" ? window.location.hostname : "";

  useEffect(() => {
    if (typeof window === "undefined") return;
    const normalizedProjectId = projectId ? String(projectId) : "";
    const urlPattern = /\.site\.dashtrack\.com$/;
    if (!normalizedProjectId || !urlPattern.test(hostname)) return;
    initSuperflow(SUPERFLOW_API_KEY, { projectId: normalizedProjectId });
  }, [projectId, hostname]);
};

// ─── Route normalization ──────────────────────────────────────────────────
//
// Handles three historic payload shapes:
//   1. Legacy: plain array of blocks (old format, may still exist in DB)
//   2. Enhanced: { pageId, pageIds, blocks } (current server format)
//   3. Enhanced: { designPayload: string } (JSON-stringified payload)

function normalizeRouteEntry(entry: RouteConfigValue): NormalizedRoute | null {
  if (!entry) return null;

  if (Array.isArray(entry)) {
    return { blocks: entry };
  }

  const result: NormalizedRoute = {};

  if (entry.pageId != null) {
    result.pageId = String(entry.pageId);
  }
  if (!result.pageId && entry.pageIds && entry.pageIds.length > 0) {
    result.pageId = String(entry.pageIds[0]);
  }
  if (entry.blocks && Array.isArray(entry.blocks)) {
    result.blocks = entry.blocks;
  }
  if (entry.designPayload) {
    if (typeof entry.designPayload === "string") {
      try {
        // parseDesignPayload returns { blocks: Block[] }; cast to DesignPayload
        // since the runtime value includes version but the type declaration omits it.
        result.designPayload = parseDesignPayload(entry.designPayload) as DesignPayload;
      } catch (error) {
        console.error("Failed to parse design payload", error);
        result.designPayload = {
          version: "1.0",
          blocks: [
            {
              _id: "payload_error",
              _type: "Heading",
              content: `Failed to parse design payload: ${
                error instanceof Error ? error.message : String(error)
              }`,
              styles:
                "#styles:,text-xl font-bold text-red-500 p-4 border border-red-300 bg-red-50 rounded",
            },
          ],
        };
      }
    } else {
      result.designPayload = entry.designPayload as DesignPayload;
    }
  }

  if (!result.blocks && result.designPayload) {
    result.blocks = result.designPayload.blocks;
  }

  return result;
}

// ─── Global link interceptor ──────────────────────────────────────────────
//
// Intercepts clicks on <a> elements and converts same-origin, in-routing
// links into SPA navigations via @page-speed/router. Without this, plain
// <a href="/about"> tags rendered inside @opensite/ui blocks cause full-page
// reloads — RouterProvider only handles popstate and explicit useNavigate()
// calls, not raw browser link clicks.
//
// Skips: external links, fragment-only anchors, mailto:/tel:,
//        target="_blank"/non-_self, modified clicks (Ctrl/Cmd/Shift/Alt).
// Only intercepts hrefs that match a key in `routing` — unknown paths fall
// through to normal browser navigation. The query string and hash are
// preserved so blog-list filters (?page=2&tag_slug=x) survive navigation.

const GlobalLinkInterceptor: React.FC<{ routing: RoutingConfig }> = ({
  routing,
}) => {
  const navigate = useNavigate();
  const pathname = usePathname();

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;

      const anchor = (e.target as HTMLElement).closest("a");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href) return;

      if (
        href.startsWith("http") ||
        href.startsWith("//") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:") ||
        href.startsWith("#")
      )
        return;

      if (anchor.target && anchor.target !== "_self") return;

      try {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return;

        const targetPathname = url.pathname;
        if (routing[targetPathname] !== undefined) {
          e.preventDefault();
          navigate(`${targetPathname}${url.search}${url.hash}`);
        }
      } catch {
        // Malformed href — let browser handle normally
      }
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [navigate, routing]);

  useEffect(() => {
    scrollToTop();
  }, [pathname]);

  return null;
};

// ─── Semantic block normalizer ────────────────────────────────────────────
//
// Blocks from dashtrack-ai's routing config arrive in semantic format:
//   { block_name, block_ref, data }
// BlocksRenderer requires ChaiBuilder format:
//   { _type, _id, blockProps }
//
// Without a proper _id, getChildBlocks(blocks, undefined) in @page-speed/blocks
// matches ALL blocks as children of every block — causing infinite recursion
// and thousands of "Error rendering block undefined of type undefined" errors.
//
// Blocks already in ChaiBuilder format (_type + _id present) pass through
// unchanged, preserving backwards compatibility with legacy DB payloads.

function normalizeBlocks(blocks: any[]): any[] {
  return blocks.map((block, index) => {
    if (block._type && block._id) return block; // already ChaiBuilder format
    // block_ref uses "category/component-id" (e.g. "hero/hero-simple-centered-image").
    // @opensite/ui registry keys are just "component-id" without the category prefix.
    const rawRef = block.block_ref ?? block.block_name ?? "Box";
    const type = rawRef.includes("/") ? rawRef.split("/").pop()! : rawRef;
    return {
      _type: type,
      _id: `block-${type}-${index}`,
      blockProps: block.data ?? {},
    };
  });
}

// ─── Feed / dynamic-route helpers ─────────────────────────────────────────

function resolveFeedConfig(website: WebsiteMetadata | null): FeedConfig | null {
  const injected =
    typeof window !== "undefined" ? window._dashtrackFeeds : undefined;
  const baseUrl = injected?.baseUrl;
  const websiteToken = injected?.websiteToken ?? website?.token ?? undefined;
  if (!baseUrl || !websiteToken) return null;
  return { baseUrl, websiteToken };
}

type QueryParamValue = string | string[];
type QueryParams = Record<string, QueryParamValue>;

function scalarQueryValue(value: QueryParamValue | undefined): string | undefined {
  return typeof value === "string" ? value : undefined;
}

// Accept both repeated bare keys (`tag_slug=a&tag_slug=b`) and Rails-style
// bracket keys (`tag_slug[]=a&tag_slug[]=b`). Preserve first-seen ordering,
// drop blanks, and de-duplicate before forwarding the symbolic filter.
function queryValues(params: QueryParams, key: string): string[] {
  const values = [params[key], params[`${key}[]`]].flatMap((value) =>
    Array.isArray(value) ? value : value === undefined ? [] : [value]
  );

  return Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
}

// Stable string for the current query params (useSearchParams returns a fresh
// object each render, so a primitive key is needed for memo/effect deps).
function stableSearchKey(params: QueryParams): string {
  return JSON.stringify(
    Object.keys(params)
      .sort()
      .map((key) => {
        const value = params[key];
        return [key, Array.isArray(value) ? value : [value]];
      })
  );
}

// Exact routing lookup with trailing-slash variant fallback (FEED_CONTRACT §5.4).
function lookupExactEntry(
  routing: RoutingConfig,
  pathname: string
): RouteConfigValue | undefined {
  if (routing[pathname] !== undefined) return routing[pathname];
  const alt = pathname.endsWith("/")
    ? pathname.replace(/\/+$/, "")
    : `${pathname}/`;
  if (alt && alt !== pathname && routing[alt] !== undefined) return routing[alt];
  return undefined;
}

function isNotFoundEntry(entry: RouteConfigValue): boolean {
  return (
    !!entry && !Array.isArray(entry) && (entry as EnhancedRouteEntry).notFound === true
  );
}

// Chrome the server harvested for this entry, normalized to a pair of arrays.
// Undefined when the entry carries none (pre-deploy configs, or a site whose
// route entries have no navbar/footer to harvest).
function entryChrome(entry: RouteConfigValue): EntryChrome | undefined {
  if (!entry || Array.isArray(entry)) return undefined;
  const chrome = (entry as EnhancedRouteEntry).chrome;
  if (!chrome) return undefined;

  const header = Array.isArray(chrome.header) ? chrome.header : [];
  const footer = Array.isArray(chrome.footer) ? chrome.footer : [];
  if (!header.length && !footer.length) return undefined;

  return { header, footer };
}

function hasBlogFeedSource(blocks: any[]): boolean {
  return blocks.some((b) => b?.dataSource?.type === "blog_feed");
}

// True when the URL carries list filters that differ from the hydrated default
// (page 1, no category/tag) — the trigger to re-query client-side (§5.4).
function listFiltersActive(params: QueryParams): boolean {
  const page = parseInt(scalarQueryValue(params.page) ?? "", 10);
  return (
    (Number.isFinite(page) && page > 1) ||
    queryValues(params, "category_slug").length > 0 ||
    queryValues(params, "tag_slug").length > 0
  );
}

// Merge URL filters into each blog_feed dataSource before re-resolving. Filters
// persist across pages because every one is re-applied on every request
// (FeedClient guarantees the wire side — legacy bug #1).
function applyListFilters(
  blocks: any[],
  params: QueryParams
): any[] {
  const page = parseInt(scalarQueryValue(params.page) ?? "", 10);
  const categorySlugs = queryValues(params, "category_slug");
  const tagSlugs = queryValues(params, "tag_slug");

  return blocks.map((block) => {
    const ds = block?.dataSource;
    if (!ds || ds.type !== "blog_feed") return block;

    const nextDs = { ...ds };
    const limit = typeof nextDs.limit === "number" ? nextDs.limit : 12;
    if (Number.isFinite(page) && page > 1) nextDs.offset = (page - 1) * limit;
    if (categorySlugs.length > 0) {
      nextDs.category = categorySlugs.length === 1 ? categorySlugs[0] : categorySlugs;
    }
    if (tagSlugs.length > 0) {
      nextDs.tag = tagSlugs.length === 1 ? tagSlugs[0] : tagSlugs;
    }
    return { ...block, dataSource: nextDs };
  });
}

// A blog_post {current:true} block whose resolution reported the record is
// missing — the client-side signal for the §5.4 case-3 not-found state.
function detailRecordMissing(blocks: any[]): boolean {
  return blocks.some(
    (b) =>
      b?.dataSource?.type === "blog_post" &&
      b?.dataSource?.current &&
      b?._feedMeta?.reason === "post_not_found"
  );
}

function deepCloneBlocks(blocks: any[] | null | undefined): any[] {
  return JSON.parse(JSON.stringify(blocks ?? []));
}

// ─── Resolution plan (FEED_CONTRACT §5.4) ─────────────────────────────────

type RenderState =
  | { kind: "blocks"; blocks: any[] }
  | { kind: "loading" }
  // `chrome` (server-harvested navbar/footer) is rendered around the 404 state so
  // a missing blog post still looks like the site it belongs to.
  | { kind: "notfound"; chrome?: EntryChrome }
  | { kind: "empty" };

type ResolutionPlan =
  | { kind: "sync"; state: RenderState }
  | {
      kind: "fetch";
      blocks: any[];
      detectMissing: boolean;
      pending: RenderState;
      onError: RenderState;
    };

function planResolution(
  routing: RoutingConfig,
  pathname: string,
  descriptors: RouteDescriptor[],
  templates: DetailTemplates,
  params: QueryParams,
  feedConfig: FeedConfig | null
): ResolutionPlan {
  // 1. Exact server-injected entry (with trailing-slash variant).
  const exact = lookupExactEntry(routing, pathname);
  if (exact !== undefined) {
    if (isNotFoundEntry(exact)) {
      return { kind: "sync", state: { kind: "notfound", chrome: entryChrome(exact) } };
    }

    const baseBlocks = normalizeRouteEntry(exact)?.blocks ?? [];

    // Blog-list re-filtering: re-query when URL filters differ from the default.
    if (
      feedConfig &&
      baseBlocks.length > 0 &&
      hasBlogFeedSource(baseBlocks) &&
      listFiltersActive(params)
    ) {
      return {
        kind: "fetch",
        blocks: applyListFilters(deepCloneBlocks(baseBlocks), params),
        detectMissing: false,
        // Keep showing the hydrated posts while re-querying (no blank flash).
        pending: { kind: "blocks", blocks: baseBlocks },
        onError: { kind: "blocks", blocks: baseBlocks },
      };
    }

    return {
      kind: "sync",
      state: baseBlocks.length ? { kind: "blocks", blocks: baseBlocks } : { kind: "empty" },
    };
  }

  // 2. Declared `_routes` pattern match → render its template.
  if (descriptors.length > 0) {
    const patterns = descriptors.map((d) => ({ path: d.pattern }));
    const match = findMatchingRoute(pathname, patterns);
    if (match && match.isMatch) {
      const descriptor = descriptors.find((d) => matchPath(pathname, d.pattern).isMatch);
      const templateBlocks = descriptor
        ? templates[descriptor.template]?.blocks ?? []
        : [];

      // No feed config or no template → cannot hydrate; treat as not found
      // rather than falling back to the homepage (§5.4 case 3).
      if (!feedConfig || templateBlocks.length === 0) {
        return { kind: "sync", state: { kind: "notfound" } };
      }

      return {
        kind: "fetch",
        blocks: deepCloneBlocks(templateBlocks),
        detectMissing: true,
        pending: { kind: "loading" },
        onError: { kind: "notfound" },
      };
    }
  }

  // 4. No match → homepage fallback (unchanged behavior).
  const homeBlocks = normalizeRouteEntry(routing["/"] ?? null)?.blocks ?? [];
  return {
    kind: "sync",
    state: homeBlocks.length ? { kind: "blocks", blocks: homeBlocks } : { kind: "empty" },
  };
}

// ─── Not-found / loading states ───────────────────────────────────────────
//
// Rendered with inline styles (not Tailwind classes) so they display correctly
// even before/without the site's generated stylesheet. Accessible and neutral.

const centeredWrapperStyle: React.CSSProperties = {
  minHeight: "60vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "2rem",
  textAlign: "center",
  fontFamily:
    "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

const NotFoundState: React.FC = () => (
  <main role="main" aria-labelledby="dt-notfound-title" style={centeredWrapperStyle}>
    <div style={{ maxWidth: "32rem" }}>
      <p style={{ fontSize: "3rem", fontWeight: 700, margin: 0, lineHeight: 1 }}>
        404
      </p>
      <h1
        id="dt-notfound-title"
        style={{ fontSize: "1.5rem", fontWeight: 600, margin: "0.75rem 0 0.5rem" }}
      >
        Page not found
      </h1>
      <p style={{ opacity: 0.75, margin: "0 0 1.25rem" }}>
        The page you&rsquo;re looking for doesn&rsquo;t exist or may have moved.
      </p>
      <a
        href="/"
        style={{
          display: "inline-block",
          padding: "0.6rem 1.25rem",
          borderRadius: "0.5rem",
          border: "1px solid currentColor",
          textDecoration: "none",
          color: "inherit",
          fontWeight: 600,
        }}
      >
        Return home
      </a>
    </div>
  </main>
);

const FeedLoadingState: React.FC = () => (
  <div
    role="status"
    aria-live="polite"
    aria-busy="true"
    style={centeredWrapperStyle}
  >
    <span
      style={{
        position: "absolute",
        width: 1,
        height: 1,
        overflow: "hidden",
        clip: "rect(0 0 0 0)",
        whiteSpace: "nowrap",
      }}
    >
      Loading&hellip;
    </span>
    <svg
      width="40"
      height="40"
      viewBox="0 0 24 24"
      aria-hidden="true"
      style={{ opacity: 0.6 }}
    >
      <g
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="2"
      >
        <path
          strokeDasharray="64"
          strokeDashoffset="64"
          strokeOpacity=".3"
          d="M12 3c4.97 0 9 4.03 9 9c0 4.97 -4.03 9 -9 9c-4.97 0 -9 -4.03 -9 -9c0 -4.97 4.03 -9 9 -9Z"
        >
          <animate
            fill="freeze"
            attributeName="stroke-dashoffset"
            dur="1.2s"
            values="64;0"
          />
        </path>
        <path strokeDasharray="16" strokeDashoffset="16" d="M12 3c4.97 0 9 4.03 9 9">
          <animate
            fill="freeze"
            attributeName="stroke-dashoffset"
            dur="0.3s"
            values="16;0"
          />
          <animateTransform
            attributeName="transform"
            dur="1.5s"
            repeatCount="indefinite"
            type="rotate"
            values="0 12 12;360 12 12"
          />
        </path>
      </g>
    </svg>
  </div>
);

function renderBlocks(blocks: any[]) {
  const normalizedBlocks = normalizeBlocks(blocks);
  try {
    return <BlocksRenderer blocks={normalizedBlocks} />;
  } catch (error) {
    console.error("Error rendering blocks:", error, blocks);
    return (
      <div className="text-xl font-bold text-red-500 p-4 border border-red-300 bg-red-50 rounded">
        Error rendering page content:{" "}
        {error instanceof Error ? error.message : String(error)}
      </div>
    );
  }
}

// ─── Page renderer ────────────────────────────────────────────────────────
//
// Resolves the current pathname per FEED_CONTRACT §5.4:
//   1. Exact routing entry (pathname, then trailing-slash variant).
//   2. Declared `_routes` pattern → render its template; data from the
//      server-injected entry (first load) or fetched via resolveBlocks (SPA nav).
//   3. Matched pattern but record missing → not-found state (never homepage).
//   4. No match → homepage fallback.

const PageRenderer: React.FC<{
  routing: RoutingConfig;
  routeDescriptors: RouteDescriptor[];
  templates: DetailTemplates;
  feedConfig: FeedConfig | null;
}> = ({ routing, routeDescriptors, templates, feedConfig }) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const searchKey = stableSearchKey(searchParams);
  const requestKey = `${pathname}?${searchKey}`;

  const plan = useMemo(
    () =>
      planResolution(
        routing,
        pathname,
        routeDescriptors,
        templates,
        searchParams,
        feedConfig
      ),
    // searchKey stands in for searchParams (fresh object identity each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname, searchKey, routing, routeDescriptors, templates, feedConfig]
  );

  const [asyncResult, setAsyncResult] = useState<{
    key: string;
    state: RenderState;
  } | null>(null);

  useEffect(() => {
    if (plan.kind !== "fetch" || !feedConfig) return;

    let cancelled = false;
    resolveBlocks(plan.blocks as any, {
      baseUrl: feedConfig.baseUrl,
      websiteToken: feedConfig.websiteToken,
      path: pathname,
    })
      .then((resolved: any[]) => {
        if (cancelled) return;
        if (plan.detectMissing && detailRecordMissing(resolved)) {
          setAsyncResult({ key: requestKey, state: { kind: "notfound" } });
        } else {
          setAsyncResult({ key: requestKey, state: { kind: "blocks", blocks: resolved } });
        }
      })
      .catch(() => {
        if (!cancelled) setAsyncResult({ key: requestKey, state: plan.onError });
      });

    return () => {
      cancelled = true;
    };
  }, [requestKey, plan, feedConfig, pathname]);

  const state: RenderState =
    plan.kind === "fetch"
      ? asyncResult && asyncResult.key === requestKey
        ? asyncResult.state
        : plan.pending
      : plan.state;

  if (state.kind === "loading") return <FeedLoadingState />;
  if (state.kind === "notfound") {
    const chrome = state.chrome;
    if (!chrome) return <NotFoundState />;
    return (
      <>
        {chrome.header?.length ? renderBlocks(chrome.header) : null}
        <NotFoundState />
        {chrome.footer?.length ? renderBlocks(chrome.footer) : null}
      </>
    );
  }
  if (state.kind === "empty") return null;
  return renderBlocks(state.blocks);
};

// ─── App root ─────────────────────────────────────────────────────────────
//
// RouterProvider from @page-speed/router handles:
//   1. App-level SPA routing — usePathname() returns the current path,
//      PageRenderer looks up the right blocks for that route.
//   2. Internal link navigation — Pressable components inside @opensite/ui
//      blocks call useNavigate() which updates the RouterProvider state,
//      triggering a re-render with the new route's blocks.
//   3. Declared dynamic routes — `_routes` patterns are registered so the
//      router matches (specificity-ordered internally) dynamic detail URLs.

const SitePagesApp: React.FC<{
  routing: RoutingConfigWithLayout;
  website: WebsiteMetadata | null;
}> = ({ routing, website }) => {
  useSuperflow(website?.sf_project_id ?? null);

  const { _layout, _routes, _templates, ...routes } = routing;

  const routeDescriptors = useMemo<RouteDescriptor[]>(
    () =>
      Array.isArray(_routes)
        ? _routes.filter((r) => r && typeof r.pattern === "string")
        : [],
    [_routes]
  );

  const routerRoutes = useMemo<Route[]>(
    () => routeDescriptors.map((d) => ({ path: d.pattern })),
    [routeDescriptors]
  );

  const templates: DetailTemplates = _templates ?? {};
  const feedConfig = useMemo(() => resolveFeedConfig(website), [website]);

  return (
    <RouterProvider routes={routerRoutes}>
      <PageViewAnalytics websiteToken={website?.token} />
      <GlobalLinkInterceptor routing={routes} />
      {_layout?.header?.length ? (
        <BlocksRenderer blocks={normalizeBlocks(_layout.header)} />
      ) : null}
      <PageRenderer
        routing={routes}
        routeDescriptors={routeDescriptors}
        templates={templates}
        feedConfig={feedConfig}
      />
      {_layout?.footer?.length ? (
        <BlocksRenderer blocks={normalizeBlocks(_layout.footer)} />
      ) : null}
    </RouterProvider>
  );
};

// ─── Bootstrap ────────────────────────────────────────────────────────────

export function startChaiPagesApp(
  routingConfig: RoutingConfigWithLayout,
  website: WebsiteMetadata | null =
    typeof window !== "undefined" ? window._dashtrackWebsite ?? null : null
) {
  const container =
    (document.getElementById("dt-root") as HTMLElement) ||
    document.querySelector(".app-wrapper");
  if (!container) return;

  const root = createRoot(container);
  root.render(<SitePagesApp routing={routingConfig} website={website} />);

  try {
    if (
      typeof document !== "undefined" &&
      typeof requestAnimationFrame !== "undefined"
    ) {
      requestAnimationFrame(() => {
        document.dispatchEvent(new Event("dashtrack:chaiPagesLoaded"));
      });
    } else {
      document.dispatchEvent(new Event("dashtrack:chaiPagesLoaded"));
    }
  } catch (_) {}
}
